import { useRef, useState, type DragEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Spinner,
} from '@monksflow/monks-ui';
import { CircleAlert, Download, FileSpreadsheet, Info, Trash2, X } from 'lucide-react';
import { FILE_SIZE_LIMITS } from '@inference.engine/file-utils/web';
import { ANALYSIS_QUERY_KEYS } from '@/entities/analysis';
import {
  downloadDataSourceTemplate,
  IQ_DATA_SOURCE_LABELS,
  IQ_DATA_SOURCE_UPLOAD_HINTS,
  type IqDataSource,
} from '@/entities/iq-data-source';
import { notificationService } from '@/lib/services/notification.service';
import { i18n } from '@/shared/i18n';
import { formatFileSize } from '@/shared/utils/format';
import { formatDate } from '@/shared/utils/dateUtils';
import { downloadFile } from '@/shared/utils/downloadFile';
import {
  deleteDataSourceFile,
  getDataSourceFileUrl,
  uploadDataSourceFile,
} from '../../api/data-sources.api';
import type { DataSourceAttachment } from './attachment';

const ACCEPTED = '.csv,.xlsx';
const TEMPLATE_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const ACCEPTED_EXTENSIONS = ['.csv', '.xlsx'];
const MAX_BYTES = FILE_SIZE_LIMITS.ASSETS.DEFAULT;

/**
 * Why the drop zone validates rather than leaning on `accept`: that attribute only filters
 * the OS picker, so a dropped file reaches this component unchecked, and without a size gate
 * the whole body is uploaded and buffered server-side before the request is rejected.
 */
const rejectionFor = (file: File): string | null => {
  const name = file.name.toLowerCase();
  if (!ACCEPTED_EXTENSIONS.some((extension) => name.endsWith(extension))) {
    return i18n.t('analysis:dataSourcesPanel.rejectedType');
  }
  if (file.size > MAX_BYTES) {
    return i18n.t('analysis:dataSourcesPanel.rejectedSize', { size: formatFileSize(MAX_BYTES) });
  }
  return null;
};

interface DataSourceUploadProps {
  analysisId: string;
  source: IqDataSource;
  attachment: DataSourceAttachment | null;
}

export const DataSourceUpload = ({ analysisId, source, attachment }: DataSourceUploadProps) => {
  const { t } = useTranslation('analysis');
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);

  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState<{ filename: string; message: string } | null>(null);
  const [pendingReplacement, setPendingReplacement] = useState<File | null>(null);
  const [confirmingRemove, setConfirmingRemove] = useState(false);

  const name = IQ_DATA_SOURCE_LABELS[source];

  const refreshReport = () =>
    queryClient.invalidateQueries({ queryKey: ANALYSIS_QUERY_KEYS.detail(analysisId) });

  const upload = useMutation({
    mutationFn: (file: File) => uploadDataSourceFile(analysisId, source, file),
    onSuccess: () => {
      setError(null);
      setPendingReplacement(null);
      void refreshReport();
    },
    onError: (err: unknown, file) => {
      setPendingReplacement(null);
      setError({
        filename: file.name,
        message: err instanceof Error ? err.message : t('dataSourcesPanel.uploadFailedFallback'),
      });
    },
  });

  const remove = useMutation({
    mutationFn: () => deleteDataSourceFile(analysisId, source),
    onSuccess: () => {
      setConfirmingRemove(false);
      void refreshReport();
    },
    onError: () => notificationService.showError(t('dataSourcesPanel.removeFailed')),
  });

  const template = useMutation({
    mutationFn: () => downloadDataSourceTemplate(source),
    onSuccess: (blob) =>
      void downloadFile({
        blob,
        mimeType: TEMPLATE_MIME_TYPE,
        fileName: `${source}-template.xlsx`,
      }),
    onError: () => notificationService.showError(t('dataSourcesPanel.templateFailed')),
  });

  const download = useMutation({
    mutationFn: () => getDataSourceFileUrl(analysisId, source),
    // Same anchor path as the template download above, rather than `window.open`: the URL is
    // resolved behind an await, so the user-gesture token is gone and a popup blocker would
    // swallow the tab silently.
    onSuccess: (url) => void downloadFile({ url, fileName: attachment?.sourceFilename }),
    onError: () => notificationService.showError(t('dataSourcesPanel.downloadFailed')),
  });

  // A source holds one file, so a second upload is a replacement and says so first.
  const accept = (file: File | undefined) => {
    if (!file) return;
    const rejection = rejectionFor(file);
    if (rejection) {
      setError({ filename: file.name, message: rejection });
      return;
    }
    if (attachment) {
      setPendingReplacement(file);
      return;
    }
    upload.mutate(file);
  };

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(false);
    accept(event.dataTransfer.files[0]);
  };

  return (
    <div className="flex flex-col gap-5">
      <p className="text-sm text-foreground-alt">{IQ_DATA_SOURCE_UPLOAD_HINTS[source]}</p>

      <div className="flex flex-col items-start gap-3 rounded-lg border border-border bg-surface px-4 py-3.5">
        <div className="flex items-start gap-2.5">
          <Info className="mt-0.5 size-4 shrink-0 text-foreground-subtle" aria-hidden />
          <div className="flex min-w-0 flex-col gap-0.5">
            <span className="text-sm font-medium">{t('dataSourcesPanel.templateTitle')}</span>
            <span className="text-sm text-foreground-alt">
              {t('dataSourcesPanel.templateBody')}
            </span>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          disabled={template.isPending}
          onClick={() => template.mutate()}
        >
          {template.isPending ? (
            <Spinner className="mr-1.5" aria-hidden />
          ) : (
            <Download className="mr-1.5 size-4" />
          )}
          {t('dataSourcesPanel.templateDownload')}
        </Button>
      </div>

      <section className="flex flex-col gap-3.5">
        {error && (
          <div className="flex items-start gap-2.5 rounded-lg border border-red-200 bg-red-50 py-3.5 pl-4 pr-2.5 dark:border-red-900 dark:bg-red-950/30">
            <CircleAlert className="mt-0.5 size-4 shrink-0 text-red-600" aria-hidden />
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <span className="text-sm font-medium text-red-700 dark:text-red-400">
                {t('dataSourcesPanel.uploadFailedTitle', { name: error.filename })}
              </span>
              <span className="text-sm text-foreground-alt">{error.message}</span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              aria-label={t('dataSourcesPanel.dismissError')}
              className="size-8 shrink-0 p-0"
              onClick={() => setError(null)}
            >
              <X className="size-4 text-foreground-alt" />
            </Button>
          </div>
        )}

        <input
          ref={inputRef}
          type="file"
          accept={ACCEPTED}
          aria-label={t('dataSourcesPanel.fileInput')}
          className="hidden"
          onChange={(event) => {
            accept(event.target.files?.[0]);
            // Cleared so re-picking the same file after an error fires change again.
            event.target.value = '';
          }}
        />

        <div
          role="button"
          tabIndex={0}
          aria-label={t('dataSourcesPanel.dropzoneAction')}
          onClick={() => inputRef.current?.click()}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') inputRef.current?.click();
          }}
          onDragOver={(event) => {
            event.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          className={`flex cursor-pointer flex-col items-center gap-1.5 rounded-xl border-[1.5px] border-dashed px-5 py-9 text-center transition-colors ${
            dragging
              ? 'border-primary bg-accent'
              : 'border-border bg-background hover:border-primary'
          }`}
        >
          {upload.isPending ? (
            <>
              <Spinner className="mb-1 size-6" />
              <span className="text-sm">
                {t('dataSourcesPanel.uploading', { name: upload.variables.name })}
              </span>
            </>
          ) : (
            <>
              <FileSpreadsheet className="mb-1 size-8 text-foreground-alt" aria-hidden />
              <span className="text-sm">
                <span className="font-medium text-primary">
                  {t('dataSourcesPanel.dropzoneAction')}
                </span>{' '}
                {t('dataSourcesPanel.dropzoneHint')}
              </span>
              <span className="text-xs text-foreground-alt">
                {t('dataSourcesPanel.dropzoneFormats', {
                  size: formatFileSize(FILE_SIZE_LIMITS.ASSETS.DEFAULT),
                })}
              </span>
            </>
          )}
        </div>
      </section>

      {/* `attachment.uploadedBy` is deliberately not rendered: it holds the raw user id the
          API writes, and a UUID on screen is worse than no attribution. Restoring the
          uploader needs the id resolved to a name server-side. */}
      {attachment && (
        <>
          <div className="h-px shrink-0 bg-border-light" />

          <section className="flex flex-col gap-3.5">
            <div className="flex flex-col">
              <span className="text-base font-medium">{t('dataSourcesPanel.attachedTitle')}</span>
              <span className="text-sm text-foreground-alt">
                {t('dataSourcesPanel.attachedSubtitle')}
              </span>
            </div>

            <div className="flex items-center gap-3 rounded-lg border border-border-light bg-background py-3 pl-3.5 pr-3">
              <span className="grid size-8 shrink-0 place-items-center rounded-md bg-green-50 text-green-600 dark:bg-green-950/30">
                <FileSpreadsheet className="size-4" aria-hidden />
              </span>
              <div className="flex min-w-0 flex-1 flex-col">
                <span className="truncate text-sm font-medium">{attachment.sourceFilename}</span>
                <span className="text-xs text-foreground-alt">
                  {t('dataSourcesPanel.attachedMeta', {
                    size:
                      attachment.fileSizeBytes === undefined
                        ? t('dataSourcesPanel.attachedRows', { count: attachment.rowCount })
                        : formatFileSize(attachment.fileSizeBytes),
                    date: formatDate(attachment.uploadedAt),
                  })}
                </span>
              </div>
              <div className="flex shrink-0 items-center gap-0.5">
                <Button
                  variant="ghost"
                  size="sm"
                  className="size-8 p-0"
                  aria-label={t('dataSourcesPanel.download')}
                  title={
                    attachment.assetId
                      ? t('dataSourcesPanel.download')
                      : t('dataSourcesPanel.downloadUnavailable')
                  }
                  disabled={!attachment.assetId || download.isPending}
                  onClick={() => download.mutate()}
                >
                  <Download className="size-4 text-foreground-alt" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="size-8 p-0"
                  aria-label={t('dataSourcesPanel.remove')}
                  onClick={() => setConfirmingRemove(true)}
                >
                  <Trash2 className="size-4 text-foreground-alt" />
                </Button>
              </div>
            </div>
          </section>
        </>
      )}

      <Dialog
        open={pendingReplacement !== null}
        onOpenChange={(open) => !open && setPendingReplacement(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('dataSourcesPanel.replaceTitle', { name })}</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <p className="text-sm text-foreground-alt">
              {t('dataSourcesPanel.replaceDescription', {
                name,
                filename: pendingReplacement?.name ?? '',
              })}
            </p>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingReplacement(null)}>
              {t('dataSourcesPanel.cancel')}
            </Button>
            <Button
              disabled={upload.isPending}
              onClick={() => pendingReplacement && upload.mutate(pendingReplacement)}
            >
              {upload.isPending && <Spinner className="mr-1.5" aria-hidden />}
              {t('dataSourcesPanel.replaceConfirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmingRemove} onOpenChange={setConfirmingRemove}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('dataSourcesPanel.removeTitle', { name })}</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <p className="text-sm text-foreground-alt">{t('dataSourcesPanel.removeDescription')}</p>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmingRemove(false)}>
              {t('dataSourcesPanel.cancel')}
            </Button>
            <Button
              variant="destructive"
              disabled={remove.isPending}
              onClick={() => remove.mutate()}
            >
              {remove.isPending && <Spinner className="mr-1.5" aria-hidden />}
              {t('dataSourcesPanel.removeConfirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

DataSourceUpload.displayName = 'DataSourceUpload';
